import * as THREE from 'three';
import { LANE_WIDTH, TRACK } from '../game/tuning';
import { mergeBoxes } from '../utils/geometry';
import { PALETTE } from '../assets/palette';

/**
 * Recycled suspended-highway segments sliding toward +Z (moving-world model).
 * Shared geometries/materials; each segment is a light Group of meshes.
 */
export class TrackSystem {
  readonly group = new THREE.Group();

  private readonly segments: THREE.Group[] = [];
  private readonly arches: Array<{ segment: THREE.Group; meshes: THREE.Mesh[] }> = [];
  private readonly geometries: THREE.BufferGeometry[] = [];
  private readonly materials: THREE.Material[] = [];
  private readonly textures: THREE.Texture[] = [];

  constructor() {
    const track = <T extends THREE.BufferGeometry>(g: T): T => {
      this.geometries.push(g);
      return g;
    };
    const mat = <T extends THREE.Material>(m: T): T => {
      this.materials.push(m);
      return m;
    };

    const deckTexture = buildDeckTexture();
    this.textures.push(deckTexture);
    const deckMaterial = mat(
      new THREE.MeshStandardMaterial({
        map: deckTexture,
        color: '#c8ccdd',
        roughness: 0.42,
        metalness: 0.72,
      }),
    );
    const edgeMaterial = mat(
      new THREE.MeshStandardMaterial({ color: PALETTE.deckEdge, roughness: 0.5, metalness: 0.6 }),
    );
    const railGlowMaterial = mat(
      new THREE.MeshStandardMaterial({
        color: '#083038',
        emissive: PALETTE.playerAccent,
        emissiveIntensity: 1.6,
        roughness: 0.4,
      }),
    );
    const laneLineMaterial = mat(
      new THREE.MeshStandardMaterial({
        color: PALETTE.laneLine,
        emissive: PALETTE.laneLine,
        emissiveIntensity: 0.9,
        roughness: 0.6,
      }),
    );
    const pylonMaterial = mat(
      new THREE.MeshStandardMaterial({ color: '#0a0a14', roughness: 0.7, metalness: 0.4 }),
    );

    const L = TRACK.segmentLength;
    const sideX = TRACK.halfWidth + 0.25;
    const deckGeometry = track(new THREE.BoxGeometry(TRACK.halfWidth * 2, 0.5, L));
    // Both side edges (and both glow rails) merged into single draws.
    const edgeGeometry = track(
      mergeBoxes([
        { geo: new THREE.BoxGeometry(0.5, 0.72, L), pos: [-sideX, 0, 0] },
        { geo: new THREE.BoxGeometry(0.5, 0.72, L), pos: [sideX, 0, 0] },
      ]),
    );
    const railGlowGeometry = track(
      mergeBoxes([
        { geo: new THREE.BoxGeometry(0.09, 0.09, L), pos: [-sideX, 0, 0] },
        { geo: new THREE.BoxGeometry(0.09, 0.09, L), pos: [sideX, 0, 0] },
      ]),
    );
    const pylonGeometry = track(new THREE.BoxGeometry(0.9, 14, 0.9));
    const braceGeometry = track(new THREE.BoxGeometry(TRACK.halfWidth * 2 + 1.4, 0.35, 0.35));

    // All lane dashes of a segment merged into one geometry (one draw call).
    const dashParts: Array<{ geo: THREE.BoxGeometry; pos: [number, number, number] }> = [];
    for (const lineX of [-LANE_WIDTH / 2, LANE_WIDTH / 2]) {
      for (let d = 0; d < L / 3; d += 1) {
        dashParts.push({
          geo: new THREE.BoxGeometry(0.08, 0.02, 1.7),
          pos: [lineX, 0.01, -L / 2 + 1.5 + d * 3],
        });
      }
    }
    const dashGeometry = track(mergeBoxes(dashParts));

    // Overhead light arch: side posts + top beam merged, plus an emissive
    // underside strip that reads as a speed gate rushing past.
    const archX = TRACK.halfWidth + 0.7;
    const archGeometry = track(
      mergeBoxes([
        { geo: new THREE.BoxGeometry(0.32, 3.4, 0.32), pos: [-archX, 1.7, 0] },
        { geo: new THREE.BoxGeometry(0.32, 3.4, 0.32), pos: [archX, 1.7, 0] },
        { geo: new THREE.BoxGeometry(archX * 2 + 0.32, 0.34, 0.4), pos: [0, 3.55, 0] },
      ]),
    );
    const archStripGeometry = track(new THREE.BoxGeometry(archX * 2 - 0.4, 0.08, 0.12));

    const holoGeometry = track(new THREE.PlaneGeometry(2.3, 1.5));
    const holoTextureA = buildHoloTexture('NEON', '#22e6ff');
    const holoTextureB = buildHoloTexture('DRIFT', '#ff4db8');
    this.textures.push(holoTextureA, holoTextureB);
    const holoMaterialA = mat(
      new THREE.MeshBasicMaterial({
        map: holoTextureA,
        transparent: true,
        opacity: 0.9,
        side: THREE.DoubleSide,
        depthWrite: false,
      }),
    );
    const holoMaterialB = mat(
      new THREE.MeshBasicMaterial({
        map: holoTextureB,
        transparent: true,
        opacity: 0.9,
        side: THREE.DoubleSide,
        depthWrite: false,
      }),
    );

    for (let i = 0; i < TRACK.segmentCount; i += 1) {
      const segment = new THREE.Group();

      const deck = new THREE.Mesh(deckGeometry, deckMaterial);
      deck.position.y = -0.25;
      deck.receiveShadow = true;
      segment.add(deck);

      const edges = new THREE.Mesh(edgeGeometry, edgeMaterial);
      edges.position.y = 0.1;
      segment.add(edges);

      const rails = new THREE.Mesh(railGlowGeometry, railGlowMaterial);
      rails.position.y = 0.5;
      segment.add(rails);

      const dashes = new THREE.Mesh(dashGeometry, laneLineMaterial);
      segment.add(dashes);

      // Suspension: pylon dropping into the void + cross brace under the deck.
      const pylon = new THREE.Mesh(pylonGeometry, pylonMaterial);
      pylon.position.set(0, -7.4, 0);
      segment.add(pylon);
      const brace = new THREE.Mesh(braceGeometry, pylonMaterial);
      brace.position.set(0, -0.7, 0);
      segment.add(brace);

      if (i % 4 === 0) {
        const arch = new THREE.Mesh(archGeometry, pylonMaterial);
        segment.add(arch);
        const strip = new THREE.Mesh(archStripGeometry, railGlowMaterial);
        strip.position.set(0, 3.32, 0);
        segment.add(strip);
        this.arches.push({ segment, meshes: [arch, strip] });
      }

      // Floating holo-boards flanking the highway: non-box set dressing.
      if (i % 8 === 2) {
        const side = i % 16 === 2 ? -1 : 1;
        const holo = new THREE.Mesh(holoGeometry, side < 0 ? holoMaterialA : holoMaterialB);
        holo.position.set(side * (TRACK.halfWidth + 2.6), 2.5, 0);
        holo.rotation.y = side * -0.55;
        segment.add(holo);
      }

      segment.position.z = TRACK.spawnZ + i * L;
      this.segments.push(segment);
      this.group.add(segment);
    }
  }

  /** Slide segments toward the player and recycle them past the camera. */
  update(distanceDelta: number): void {
    const span = TRACK.segmentCount * TRACK.segmentLength;
    for (const segment of this.segments) {
      segment.position.z += distanceDelta;
      if (segment.position.z > TRACK.despawnZ + TRACK.segmentLength / 2) {
        segment.position.z -= span;
      }
    }
    // Hide arches once they reach the player so the beam never blocks the view.
    for (const { segment, meshes } of this.arches) {
      const visible = segment.position.z < -1.5;
      for (const mesh of meshes) mesh.visible = visible;
    }
  }

  dispose(): void {
    for (const g of this.geometries) g.dispose();
    for (const m of this.materials) m.dispose();
    for (const t of this.textures) t.dispose();
  }
}

/** Deck albedo: dark plates with panel seams, hatch cuts and faint scuffs. */
function buildDeckTexture(): THREE.CanvasTexture {
  const canvas = document.createElement('canvas');
  canvas.width = 256;
  canvas.height = 256;
  const ctx = canvas.getContext('2d')!;
  ctx.fillStyle = '#0d0d19';
  ctx.fillRect(0, 0, 256, 256);

  // Panel seams: a 2x3 plate grid with slightly lit edges.
  ctx.strokeStyle = 'rgba(70, 90, 130, 0.5)';
  ctx.lineWidth = 2;
  for (const x of [0, 128, 256]) {
    ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, 256); ctx.stroke();
  }
  for (const y of [0, 85, 170, 256]) {
    ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(256, y); ctx.stroke();
  }
  // Inner hatch cuts on alternate plates.
  ctx.strokeStyle = 'rgba(40, 52, 84, 0.55)';
  ctx.lineWidth = 1.5;
  ctx.strokeRect(16, 14, 96, 57);
  ctx.strokeRect(144, 99, 96, 57);
  ctx.strokeRect(16, 184, 96, 57);
  // Faint scuff streaks along travel direction.
  ctx.strokeStyle = 'rgba(120, 140, 170, 0.10)';
  const lcgScuff = { s: 9 };
  const rnd = () => {
    lcgScuff.s = (lcgScuff.s * 1664525 + 1013904223) >>> 0;
    return lcgScuff.s / 0xffffffff;
  };
  for (let i = 0; i < 26; i += 1) {
    const x = rnd() * 256;
    const y = rnd() * 256;
    ctx.lineWidth = 0.8 + rnd() * 1.6;
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x + (rnd() - 0.5) * 8, y + 18 + rnd() * 42);
    ctx.stroke();
  }

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.repeat.set(2, 3);
  texture.anisotropy = 4;
  return texture;
}

/** Neon holo-board: glyph text with chevrons on a translucent panel. */
function buildHoloTexture(text: string, color: string): THREE.CanvasTexture {
  const canvas = document.createElement('canvas');
  canvas.width = 192;
  canvas.height = 128;
  const ctx = canvas.getContext('2d')!;
  ctx.clearRect(0, 0, 192, 128);
  ctx.fillStyle = 'rgba(8, 12, 28, 0.55)';
  ctx.fillRect(6, 8, 180, 112);
  ctx.strokeStyle = color;
  ctx.lineWidth = 3;
  ctx.strokeRect(6, 8, 180, 112);
  ctx.fillStyle = color;
  ctx.font = '800 44px "Rajdhani", "Segoe UI", sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, 96, 52);
  // Chevron row underneath.
  ctx.font = '700 30px "Segoe UI", sans-serif';
  ctx.fillText('› › ›', 96, 94);
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
}
